import axiosInstance from 'config/axiosInstance';
import { MAX_PAGE_SIZE } from 'config/constant';

/**
 * A28（2026-11-03 和田様の UAT）。エクスポートで一度に出す記録の上限。
 *
 * 「期間を指定」「全件」は、一覧の API を1ページ MAX_PAGE_SIZE 件ずつ順に読んで画面で CSV にする。
 * 会社の記録は運転者×1日に数件の規模で、1年分でも数千件に収まる見込み。上限を超えるときは
 * 途中まで出さずに断り、期間を絞ってもらう（黙って欠けた CSV を出さない）。
 */
export const MAX_EXPORT_ROWS = 10000;

/** 出す件数が MAX_EXPORT_ROWS を超えた。 */
export class TooManyRowsError extends Error {
  readonly count: number;
  readonly max: number;

  constructor(count: number, max: number) {
    super(`too many rows: ${count} > ${max}`);
    this.name = 'TooManyRowsError';
    this.count = count;
    this.max = max;
  }
}

/**
 * 一覧の API を全ページ読んで、記録を並びのまま返す。
 *
 * buildUrl は page と page_size を入れた URL を返す（絞り込み・並び順は呼び出し側が入れる）。
 * 1ページ目の count が上限を超えたら TooManyRowsError。count の分を読み終えるか、空のページ・
 * next が無いページで止める（読んでいる間に記録が減っても無限に読まない）。途中で失敗したら例外のまま返す。
 */
export async function fetchAllPages<T = any>(
  buildUrl: (page: number, pageSize: number) => string,
  maxRows: number = MAX_EXPORT_ROWS
): Promise<T[]> {
  const rows: T[] = [];
  const maxPages = Math.ceil(maxRows / MAX_PAGE_SIZE) + 1;
  for (let page = 1; page <= maxPages; page += 1) {
    const response: { data?: { count?: number; results?: T[]; next?: string | null } } =
      await axiosInstance.get(buildUrl(page, MAX_PAGE_SIZE));
    const count = Number(response.data?.count ?? 0);
    if (page === 1 && count > maxRows) throw new TooManyRowsError(count, maxRows);
    const results = response.data?.results ?? [];
    rows.push(...results);
    if (results.length === 0 || !response.data?.next || rows.length >= count) break;
  }
  return rows;
}
